import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import React, { useEffect } from 'react';
import { View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import Animated from 'react-native-reanimated';
import { ProgressBar } from '@photosi/rn-components/progress-bar/ProgressBar';
import { stylesDesktop, stylesMobile } from './LoadingStep.style';
import { PrimaryButton } from '@photosi/rn-components/buttons/primary-button/PrimaryButton';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';
export interface LoadingStepProps {
  isUploadingPhoto?: boolean;
  onConfirmStep: (error?: boolean) => void;
}

export const LoadingStep = observer((props: LoadingStepProps) => {
  const {
    i18n,
    data: { percentage, setPercentage, uploadError },
    project: { activeProject },
  } = useStore();

  const { isUploadingPhoto, onConfirmStep } = props;
  const showError = !isUploadingPhoto && uploadError && activeProject?.selectedThumbnails?.length === 0;
  const { isDesktop } = useResponsiveScreen();
  const styles = isDesktop ? stylesDesktop : stylesMobile;

  const progressBarVisible = isUploadingPhoto || (percentage > 0 && percentage <= 100);

  useEffect(() => {
    if (percentage === 100) {
      setTimeout(() => {
        !showError && onConfirmStep();
        setPercentage(0);
      }, 500);
    }
  }, [percentage, showError]);

  return (
    <View style={styles.container}>
      {progressBarVisible ? (
        <Animated.View style={styles.progress} accessibilityLiveRegion="polite">
          <View style={styles.wrapperHeader}>
            <TextLato style={styles.headerText} numberOfLines={1}>
              {i18n.t('modal-picker-my-photos:loading')}
            </TextLato>
          </View>
          <View style={styles.wrapperHeader}>
            <ProgressBar progress={percentage} />
          </View>
          <View style={styles.wrapperFooter}>
            <TextLato style={styles.footerText} numberOfLines={1}>
              {i18n.t('modal-picker-my-photos:waiting')}
            </TextLato>
          </View>
        </Animated.View>
      ) : showError ? (
        <View style={styles.errorWrapper} accessibilityLiveRegion="assertive">
          <View style={{ width: 64, height: 64, paddingBottom: 16 }} accessible={false}>
            <Illustration name={ILLUSTRATION_NAMES.ImportFailed} />
          </View>
          <View style={styles.errorInnerWrapper}>
            <TextLato style={styles.errorTitle} accessibilityRole="header">{i18n.t('upload:upload-error-title')}</TextLato>
          </View>
          <View style={styles.errorInnerWrapper}>
            <TextLato style={styles.errorDescriptionCenter}>{i18n.t('upload:upload-error-description')}</TextLato>
          </View>
          <View style={{ paddingTop: 24 }}>
            <PrimaryButton
              onPress={async () => onConfirmStep(true)}
              style={styles.button}
              textStyle={styles.buttonText}
              testID={modalTestID(TestIDs.UPLOAD_PHOTO, TestIDs.CONTINUE)}
              focusId={modalTestID(TestIDs.UPLOAD_PHOTO, TestIDs.CONTINUE)}
              focusGroup={FocusGroups.MODAL}
              text={i18n.t('upload:retry')}
              accessibilityLabel={i18n.t('upload:retry')}
              focusOrder={0}
            />
          </View>
        </View>
      ) : null}
    </View>
  );
});
